import React, { useMemo, useState, useEffect } from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { useToast } from "../../services/utils";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams } from "react-router-dom";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import {
  deleteUser,
  activateUser,
  deactivateUser,
} from "../../services/userService";
import {
  IconEditBase,
  IconDeleteBlueSvg,
  IconEyeBase,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
  IconReactivateSvg,
} from "../../common/utils/icons";
import usePaginatedFetchUsersList from "../../hooks/usePaginatedFetchUsersList";
import useDebounce from "../../hooks/useDebounce";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import { enableRowGroupByColumnProps } from "../../components/DataGridProps/DataGridProps";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import Loader from "../../components/Molecules/Loader/Loader";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import { useAuth } from "../../contexts/AuthContext";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import { getConfig } from "../../config/runtimeConfig";
import "./Users.scss";

// Centralized Filter Configuration for Users List
const USERS_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    username: "",
    email: "",
    status_filter: "Active",
  },

  // Fields to persist in URL
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    ID_FIELDS: [],
    STRING_FIELDS: ["username", "email", "status_filter"],
    DATE_FIELDS: [],
  },
} as const;

const Users: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const { hasPermission, userInfo } = useAuth();
  const { t } = useAppTranslation();
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();

  // Permission checks for user actions
  const canViewUser = hasPermission("user", "view");
  const canEditUser = hasPermission("user", "edit");
  const canDeleteUser = hasPermission("user", "delete");

  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // SearchFilterBar state - initialize from URL parameters
  const [searchFilterData, setSearchFilterData] = useState<any>(() => {
    const initialState: any = {
      ...USERS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Restore filters from URL parameters
    USERS_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        initialState[field] = urlValue;
      }
    });

    return initialState;
  });

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [userToDelete, setUserToDelete] = useState<any>(null);

  // Activation/Deactivation state management
  const [activateConfirmOpen, setActivateConfirmOpen] =
    useState<boolean>(false);
  const [deactivateConfirmOpen, setDeactivateConfirmOpen] =
    useState<boolean>(false);
  const [userToActivate, setUserToActivate] = useState<any>(null);
  const [userToDeactivate, setUserToDeactivate] = useState<any>(null);

  // Extract filters from searchFilterData
  const effectiveStatusFilter = searchFilterData?.status_filter || "";
  const effectiveUsername = searchFilterData?.username || "";
  const effectiveEmail = searchFilterData?.email || "";

  // Debounce username and email with 400ms delay
  const debouncedUsername = useDebounce(effectiveUsername);
  const debouncedEmail = useDebounce(effectiveEmail);

  // Determine if we should include username in API call (minimum 3 characters)
  const effectiveUsernameSearch =
    debouncedUsername.trim().length >= 3 ? debouncedUsername : "";

  // Determine if we should include email in API call (minimum 3 characters)
  const effectiveEmailSearch =
    debouncedEmail.trim().length >= 3 ? debouncedEmail : "";

  // Convert status_filter to isActive parameter
  let isActiveParam = "true"; // Default to active
  if (effectiveStatusFilter === "Inactive") {
    isActiveParam = "false";
  } else if (effectiveStatusFilter === "All") {
    isActiveParam = "all";
  }

  const [usersData, refetchUsers] = usePaginatedFetchUsersList({
    setIsLoading,
    page,
    pSize,
    searchTerm: effectiveUsernameSearch,
    email: effectiveEmailSearch,
    isActive: isActiveParam,
  });

  // Sync filters to URL parameters (state → URL)
  useEffect(() => {
    const newParams = new URLSearchParams();

    USERS_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = searchFilterData[field];
      if (value !== null && value !== undefined && value !== "") {
        // Don't add default values to URL to keep it clean
        if (field === "status_filter" && value === "Active") {
          return; // Skip default status filter
        }
        newParams.set(field, String(value));
      }
    });

    // Only update URL if params have changed
    const currentParamsString = searchParams.toString();
    const newParamsString = newParams.toString();
    if (currentParamsString !== newParamsString) {
      setSearchParams(newParams, { replace: true });
    }
  }, [searchFilterData, searchParams, setSearchParams]);

  // Update filter state when URL changes (URL → state for back/forward navigation)
  useEffect(() => {
    const urlFilterState: any = {
      ...USERS_FILTER_CONFIG.DEFAULT_STATE,
    };

    // Extract filter parameters from URL
    USERS_FILTER_CONFIG.URL_FIELDS.forEach((field: string) => {
      const urlValue = searchParams.get(field);
      if (urlValue) {
        urlFilterState[field] = urlValue;
      }
    });

    // Check if URL filter state differs from current filter state
    const hasFilterChanges = Object.keys(urlFilterState).some((key) => {
      const urlValue = urlFilterState[key];
      const currentValue = searchFilterData[key];

      // Handle null/undefined/empty string comparisons
      const normalizedUrlValue =
        urlValue === null || urlValue === undefined || urlValue === ""
          ? ""
          : urlValue;
      const normalizedCurrentValue =
        currentValue === null ||
        currentValue === undefined ||
        currentValue === ""
          ? ""
          : currentValue;

      return normalizedUrlValue !== normalizedCurrentValue;
    });

    // Only update state if there are actual changes to prevent infinite loops
    if (hasFilterChanges) {
      setSearchFilterData(urlFilterState);
    }
  }, [searchParams]); // Only depend on searchParams to detect URL changes

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [effectiveUsernameSearch, effectiveEmailSearch, effectiveStatusFilter]);

  // Handle delete user
  const handleDeleteUser = (user: any) => {
    setUserToDelete(user);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      setIsLoading(true);
      const result = await deleteUser(userToDelete.id);
      if (result) {
        showToast(t("users.toast.deleted"), "success");
      }

      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      // Refetch the users list to update the UI
      refetchUsers();
    } catch (error: any) {
      console.error("Error deleting user:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle status toggle from switch
  const handleStatusToggle = (user: any, newStatus: boolean) => {
    if (newStatus) {
      // User is being activated
      setUserToActivate(user);
      setActivateConfirmOpen(true);
    } else {
      // User is being deactivated
      setUserToDeactivate(user);
      setDeactivateConfirmOpen(true);
    }
  };

  // Confirm activate action
  const confirmActivate = async () => {
    if (!userToActivate) return;

    try {
      setIsLoading(true);
      const result = await activateUser(userToActivate.id);
      if (result) {
        showToast(t("users.toast.activated"), "success");
      }
      setActivateConfirmOpen(false);
      setUserToActivate(null);
      // Refetch the users list to update the UI
      refetchUsers();
    } catch (error: any) {
      console.error("Error activating user:", error);
      showToast(t("users.toast.failedActivate"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm deactivate action
  const confirmDeactivate = async () => {
    if (!userToDeactivate) return;

    try {
      setIsLoading(true);
      await deactivateUser(userToDeactivate.id);
      showToast(t("users.toast.deactivated"), "success");
      setDeactivateConfirmOpen(false);
      setUserToDeactivate(null);
      // Refetch the users list to update the UI
      refetchUsers();
    } catch (error: any) {
      console.error("Error deactivating user:", error);
      showToast(t("users.toast.failedDeactivate"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle reactivate user
  const handleReactivate = async (user: any) => {
    try {
      setIsLoading(true);
      await activateUser(user.id);
      showToast(t("users.toast.reactivated"), "success");
      // Refetch the users list to update the UI
      refetchUsers();
    } catch (error: any) {
      console.error("Error reactivating user:", error);
      showToast(
        error?.response?.data?.detail || t("users.toast.failedReactivate"),
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Create status column with icons only
  const statusColumn: GridColDef = {
    field: "is_active",
    headerName: t("users.list.columns.status"),
    width: 150,
    sortable: false,
    align: "center",
    headerAlign: "center",
    renderCell: (params: any) => {
      return (
        <span>
          {params.value ? (
            <Tooltip title={t("common.status.active")} placement="top" arrow>
              <Box>
                <IconCheckCircleIconGreenSvg width={21} height={21} />
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title={t("common.status.inactive")} placement="top" arrow>
              <Box>
                <IconCrossCircleIconSvg width={21} height={21} />
              </Box>
            </Tooltip>
          )}
        </span>
      );
    },
  };

  const baseColumns: GridColDef[] = [
    {
      ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
      width: 1,
      minWidth: 20,
      flex: 0,
      renderCell: () => <></>,
      cellClassName: "no-padding-cell",
      headerClassName: "no-padding-cell",
    },
    {
      field: "id",
      headerName: t("users.list.columns.id"),
      width: 100,
      sortable: true,
    },
    {
      field: "username",
      headerName: t("users.list.columns.username"),
      width: 100,
      flex: 1,
      sortable: true,
    },
    {
      field: "email",
      headerName: t("users.list.columns.email"),
      width: 200,
      flex: 1,
      sortable: true,
      renderCell: (params: any) => (
        <LimitChractersWithTooltip value={params.value || ""} />
      ),
    },
    {
      field: "roles",
      headerName: t("users.list.columns.roles"),
      width: 120,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.value?.length || 0}</span>,
    },
    {
      field: "systems",
      headerName: t("users.list.columns.systems"),
      width: 120,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.value?.length || 0}</span>,
    },
    {
      field: "tags",
      headerName: t("users.list.columns.tags"),
      width: 120,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.value?.length || 0}</span>,
    },
    {
      field: "groups",
      headerName: t("users.list.columns.groups"),
      width: 120,
      sortable: true,
      flex: 1,
      renderCell: (params: any) => <span>{params.value?.length || 0}</span>,
    },

    {
      field: "actions",
      headerName: t("users.list.columns.actions"),
      align: "center",
      headerAlign: "center",
      width: 160,
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params: any) => {
        const isUserActive = params.row.is_active;

        // For inactive users, show only the Reactivate button
        if (!isUserActive) {
          return (
            <>
              {canEditUser && (
                <Tooltip
                  title={t("users.actions.reactivate")}
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() => handleReactivate(params.row)}
                  >
                    <IconReactivateSvg />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        }

        // For active users, show View, Edit, and Delete buttons
        return (
          <>
            {canViewUser && (
              <Tooltip title={t("users.actions.view")} placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => navigate(`/users/view/${params.row.id}`)}
                >
                  <IconEyeBase />
                </IconButton>
              </Tooltip>
            )}
            {canEditUser && (
              <Tooltip title={t("users.actions.edit")} placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => navigate(`/users/edit/${params.row.id}`)}
                >
                  <IconEditBase />
                </IconButton>
              </Tooltip>
            )}
            {canDeleteUser && (
              <Tooltip title={t("users.actions.delete")} placement="top" arrow>
                <IconButton
                  className="datagrid-action-btn"
                  color="inherit"
                  onClick={() => handleDeleteUser(params.row)}
                  disabled={params.row.id === userInfo?.id}
                  sx={{ opacity: params.row.id === userInfo?.id ? 0.5 : 1 }}
                >
                  <IconDeleteBlueSvg />
                </IconButton>
              </Tooltip>
            )}
          </>
        );
      },
    },
  ];

  // Create final columns array with status column always included before actions
  const columns: GridColDef[] = useMemo(() => {
    const columnsBeforeActions = baseColumns.slice(0, -1); // All columns except actions
    const actionsColumn = baseColumns[baseColumns.length - 1]; // Actions column

    // Always include the status column before actions
    return [...columnsBeforeActions, statusColumn, actionsColumn];
  }, [baseColumns, statusColumn]);

  return (
    <Box className="users-page">
      <Loader isLoading={isLoading} />

      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle={t("users.dialog.deleteTitle")}
        dailogDescription={t("users.dialog.deleteDescription", {
          username: userToDelete?.username,
        })}
      />

      <ConfirmDailog
        isDailogOpen={activateConfirmOpen}
        handleCancelAction={() => setActivateConfirmOpen(false)}
        handleConfirmAction={confirmActivate}
        dailogTitle={t("users.dialog.activateTitle")}
        dailogDescription={t("users.dialog.activateDescription", {
          username: userToActivate?.username,
        })}
      />

      <ConfirmDailog
        isDailogOpen={deactivateConfirmOpen}
        handleCancelAction={() => setDeactivateConfirmOpen(false)}
        handleConfirmAction={confirmDeactivate}
        dailogTitle={t("users.dialog.deactivateTitle")}
        dailogDescription={t("users.dialog.deactivateDescription", {
          username: userToDeactivate?.username,
        })}
      />

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={["username", "email", "status_filter"]}
              primaryFields={["username", "email", "status_filter"]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
              }}
            />
          </Grid>
          {/*isAdmin && (
            <Grid
              item
              xs="auto"
              sx={{ display: "flex", alignItems: "flex-start" }}
            >
              <ButtonComponent
                handelClickEvent={() => navigate("/users/add")}
                className="btn-orange"
              >
                <>
                  <AddSharpIcon sx={{ marginRight: "4px" }} /> User
                </>
              </ButtonComponent>
            </Grid>
          )*/}
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={usersData?.items || []}
          loading={isLoading}
          dataListTitle={t("users.list.title")}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={usersData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          singlePageMaxHeightDiff={360}
        />
      </FlexBetween>
    </Box>
  );
};

export default Users;
